import type { ReactNode } from 'react'

/** Shared look for actions in a detail pane (links and buttons). */
export const detailActionClass =
  'inline-flex items-center gap-1.5 rounded-md border border-line-strong bg-surface-raised px-2.5 py-1 text-[12px] text-foreground transition-colors hover:bg-surface-active disabled:pointer-events-none disabled:opacity-60'

/** Destructive action (remove, revoke, delete): outlined red, never the amber accent. */
export const detailDangerClass =
  'inline-flex items-center gap-1.5 rounded-md border border-danger bg-transparent px-2.5 py-1 text-[12px] text-danger transition-colors hover:bg-danger/10 disabled:pointer-events-none disabled:opacity-60'

/** Content of the right-hand detail pane for the selected row. */
export function DetailPane({ eyebrow, title, status, actions, children }: {
  /** what kind of thing this is (Rule, Project…), small caps above the title */
  eyebrow?: string
  title: ReactNode
  /** status line under the title */
  status?: ReactNode
  actions?: ReactNode
  children?: ReactNode
}) {
  return (
    <div className="px-4 py-3.5 text-[12.5px]">
      {eyebrow && (
        <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.07em] text-text-3">{eyebrow}</div>
      )}
      <h2 className="break-words pr-7 text-[14px] font-semibold leading-5 text-foreground">{title}</h2>
      {status && <div className="mt-0.5">{status}</div>}
      {children && <div className="mt-3 space-y-4">{children}</div>}
      {actions && <div className="mt-4 flex flex-wrap gap-2">{actions}</div>}
    </div>
  )
}

/** Labelled block inside a detail pane. */
export function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.07em] text-text-3">{title}</h3>
      {children}
    </section>
  )
}

/** Two-column key/value list. */
export function KeyValue({ items }: { items: [label: string, value: ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-2.5 gap-y-1.5">
      {items.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-text-3">{k}</dt>
          <dd className="min-w-0 break-words text-foreground">{v}</dd>
        </div>
      ))}
    </dl>
  )
}
